import { useState } from 'react';
import { getMessage } from '@tcc/i18n';
import classNames from 'classnames';

import Tooltip from '../tooltip/tooltip';
import { toDateString } from '../../../utils/date';
import { PLANET_LABS_IMAGERY_POC, PLANET_LABS_IMAGERY_8_BITS } from '../../../constants/imagery';

import styles from './FHAEvent.css';

export type FHAEventProps = {
  date: string,
  eventIndex: number,
  gdus?: string,
  hasHistoricWeather: boolean,
  id: string,
  imageUrl: string,
  isActive: boolean,
  locale: string,
  lowPercent?: string,
  onClick: () => void,
  plantingDate?: string,
  precipitation: string,
  provider: string,
  showImageryProvider: boolean,
  microsoftGDDGlobalRelease: boolean
};

export function FHAEvent({
    id,
    date,
    gdus,
    locale,
    onClick,
    provider,
    isActive,
    imageUrl,
    eventIndex,
    lowPercent,
    plantingDate,
    precipitation = '0"',
    hasHistoricWeather,
    showImageryProvider,
    microsoftGDDGlobalRelease
  }: FHAEventProps) {
  const [openTooltip, setOpenTooltip] = useState(false);

  const time = toDateString(date, locale);
  const shouldShowPrecip = !plantingDate || date >= plantingDate;
  const isRecentPrecipAvailable = !(eventIndex === 0 && precipitation === '0"');

  const showPlanetImageryProvider = showImageryProvider && (provider === PLANET_LABS_IMAGERY_8_BITS || provider === PLANET_LABS_IMAGERY_POC);

  return (
    <div
      className={classNames(styles.cardContainer, {
        [styles.active]: isActive,
        'planet-labs-poc': showPlanetImageryProvider
      })}
      data-test-id={showPlanetImageryProvider ? 'planet-labs-poc' : `FHA-card-${eventIndex}`}
      id={id}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className={styles.headerContainer}>
        <div className={styles.imageCard}>
          <img
            alt={getMessage('image.fha.card')}
            data-test-id={`FHA-image-card-${eventIndex}`}
            src={imageUrl}
          />
        </div>
        <div className={styles.dateContainer}>
          <time
            data-test-id={`FHA-image-date-card-${eventIndex}`}
            dateTime={date}
          >
            {time}
          </time>
          <div>
            <div
              className={styles.imageDate}
              data-test-id={`FHA-image-date-card-subtitle-${eventIndex}`}
            >
              {showPlanetImageryProvider ? getMessage('card.image.providedByPlanet') : ''}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.dataContainer}>
        {lowPercent !== undefined && (
          <div
            className={styles.data}
            data-test-id={`FHA-lowPercent-title-${eventIndex}`}
          >
            <div
              className={styles.lowBiomass}
              data-test-id={`FHA-card-lowBiomass-label-${eventIndex}`}
              onMouseLeave={() => setOpenTooltip(false)}
              onMouseOver={() => setOpenTooltip(true)}
              onBlur={() => setOpenTooltip(false)}
              onFocus={() => setOpenTooltip(true)}
            >
              {openTooltip && (
                <Tooltip text={getMessage('card.fha.lowBiomassExplanation')} />
              )}
              {getMessage('card.fha.lowBiomass')}
            </div>
            <div className={styles.flashingLine} />
            <div
              className={styles.value}
              data-test-id={`FHA-lowPercent-value-${eventIndex}`}
            >
              <div className={styles.square} />
              <div className={styles.lowPercent}>{lowPercent}</div>
            </div>
          </div>
        )}

        {hasHistoricWeather && (
          <div
            className={styles.data}
            data-test-id={`FHA-seasonalRainfall-title-${eventIndex}`}
          >
            <div className={styles.value}>
              {getMessage('card.fha.seasonalRainfall')}
            </div>
            <div className={styles.flashingLine} />
            <div
              className={styles.value}
              data-test-id={`FHA-seasonalRainfall-value-${eventIndex}`}
            >
              {isRecentPrecipAvailable && shouldShowPrecip ? precipitation : '---'}
            </div>
          </div>
        )}
        
        {microsoftGDDGlobalRelease || hasHistoricWeather ? (
          <div
            className={styles.data}
            data-test-id={`FHA-gdus-title-${eventIndex}`}
          >
            <div data-test-id="FHA-gdus-value">
              {getMessage('card.fha.gdus')}
            </div>
            <div className={styles.flashingLine} />
            <div
              className={styles.value}
              data-test-id={`FHA-gdus-title-value-${eventIndex}`}
            >
              <div data-test-id={`FHA-gdus-value-${eventIndex}`}>
                {gdus || '---'}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
